import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from "prop-types";
import Fragment from "common/src/components/widget/Fragment";
import Select from "common/src/components/widget/Select";
import Input from "common/src/components/widget/Input";
import Button from "common/src/components/widget/Button";
import {aggregationOperations} from "../../../model/aggregationOperations";
import {useTranslation} from "react-i18next";
import {addErrorNotification} from "../../../state/notification/notifications";
import {useDispatch} from "react-redux";
import FloatingLabelTagsWrapper from "common/src/components/widget/FloatingLabelTagsWrapper";
import {isEqual} from "lodash";
import * as Yup from 'yup';
import {useForm} from "common/src/hooks/form/formHook";

const AggregationsCard = (props) => {

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [aggregations, setAggregations] = useState(props.formField.value || []);
    const [externalValue, setExternalValue] = useState(props.formField.value || []);

    const previousAggregations = useRef(props.formField.value || []);
    const isLabelUnique = useCallback((label) => {
            if (!label) return true;

            const isUniqueInAggregations = !aggregations.some((column) => column.label === label);
            const isUniqueInRestrictedLabels = !props.restrictedLabels?.some((el) => el === label);

            return isUniqueInAggregations && isUniqueInRestrictedLabels;
        },
        [aggregations, props.restrictedLabels]
    );

    const schema = {
        label: Yup.string()
            .matches(/^[a-zA-Z0-9_$]+$/, 'Label can only contain letters, numbers, underscores, and dollar signs')
            .matches(/^[^\s]+$/, 'Spaces are not allowed in labels')
            .test('is-unique', 'Label must be unique for columns as well as aggregation aliases', function (value) {
                return isLabelUnique(value);
            })
            .required('Label is required'),
        column: Yup.string().required('Column is required'),
        operation: Yup.string().required('Aggregate operation is required'),
    };
    const {
        values,
        getField,
        handleSubmit: onAdd,
        setFormNotDirty,
        clearValidation,
        reinitSchema
    } = useForm(submit, {}, schema);

    useEffect(() => {
        if (!isEqual(props.formField.value, previousAggregations.current)) {
            setExternalValue(props.formField.value || []);
            setAggregations(props.formField.value || []);
        }
        if (!isEqual(props.formField.value, externalValue)) {
            setExternalValue(props.formField.value || [])
        }
    }, [props.formField.value]);

    useEffect(() => {
        if (!isEqual(previousAggregations.current, aggregations)) {
            previousAggregations.current = aggregations;
            if (!isEqual(aggregations, externalValue)) {
                props.formField.setValue(aggregations, undefined, true);
            }
        }
    }, [aggregations]);

    useEffect(() => {
        reinitSchema(schema);
    }, [isLabelUnique]);
    // , aggregations, props.restrictedLabels
    function submit() {
        const newAggregation = {
            columnName: values.column,
            label: values.label,
            operation: values.operation
        };

        if (isLabelUnique(newAggregation.label)) {
            setAggregations([...aggregations, newAggregation]);
            setFormNotDirty(true);
            clearValidation();
        } else {
            dispatch(addErrorNotification({
                title: t('error.validation.unique.title'),
                content: t('error.validation.unique.aggregation.label'),
            }));
        }
    }

    const getAggregationOperations = () => {
        if (!getField("column").value ||
            !props.options ||
            !props.options.find(option => option.name === getField("column").value)) {
            return [];
        }
        if (props.options && props.options.find(option => option.name === getField("column").value)?.type === "STRING") {
            return aggregationOperations
                .filter(operation => operation.code === "COUNT" || operation.code === "COUNT_DISTINCT");
        }
        if (props.options && props.options.find(option => option.name === getField("column").value)?.type === "BOOLEAN") {
            return aggregationOperations
                .filter(operation => operation.code === "COUNT" || operation.code === "COUNT_DISTINCT");
        }
        return aggregationOperations;
    }
    const handleTagRemove = (tag) => {
        setAggregations(aggregations.filter((column) =>
            !(column.columnName === tag.columnName && column.label === tag.label)));
    }
    const options = useMemo(() => {
        let filtered = (props.options?.filter(field => field.type !== "ARRAY_NUMBER") || []).slice();
        // filtered?.sort((a, b) => a != null && a.name > b.name);
        filtered.sort((a, b) => (a != null && b != null && a.name > b.name) ? 1 : -1);

        return filtered;
    }, [props.options]);
    function camelCase(input) {
        let result = input.trim();
        if (result.length === 0) {
            return "";
        }
        if (result.length === 1) {
            return result.toLowerCase();
        }
        const hasUpperCase = result !== result.toLowerCase();
        if (hasUpperCase) {
            result = preserveCamelCase(result);
        }
        return result.replace(/^[_.\- ]+/, "").toLowerCase().replace(/[_.\- ]+([\p{Alpha}\p{N}_]|$)/gu, (_, p1) => p1.toUpperCase()).replace(/\d+([\p{Alpha}\p{N}_]|$)/gu, m => m.toUpperCase());
    }
    const preserveCamelCase = string => {
        let result = string;
        let isLastCharLower = false;
        let isLastCharUpper = false;
        let isLastLastCharUpper = false;
        for (let i = 0; i < result.length; i++) {
            const character = result[i];
            if (isLastCharLower && /[\p{Lu}]/u.test(character)) {
                result = `${result.slice(0, i)}-${result.slice(i)}`;
                isLastCharLower = false;
                isLastLastCharUpper = isLastCharUpper;
                isLastCharUpper = true;
                i += 1;
            } else if (isLastCharUpper && isLastLastCharUpper && /[\p{Ll}]/u.test(character)) {
                result = `${result.slice(0, i - 1)}-${result.slice(i - 1)}`;
                isLastLastCharUpper = isLastCharUpper;
                isLastCharUpper = false;
                isLastCharLower = true;
            } else {
                isLastCharLower = character.toLowerCase() === character && character.toUpperCase() !== character;
                isLastLastCharUpper = isLastCharUpper;
                isLastCharUpper = character.toUpperCase() === character && character.toLowerCase() !== character;
            }
        }
        return result;
    };
    return (
        <div className="card">
            <header className="card-header">
                <p className="card-header-title">
                    <span className="icon fz fz-wrench medium mr-1"/>
                    <span>{t('dataset.aggregation')}</span>
                </p>
            </header>
            <div className="card-content">
                <div>

                    <FloatingLabelTagsWrapper
                        label="Selected aggregations"
                        labelKeys={[["columnName"], ["label"]]}
                        formatter={(obj) => `${aggregationOperations.find(it => it.code === obj.operation).format.replace("{}", obj.columnName)} as ${obj.label}`}
                        disabled={props.disabled}
                        tags={[...aggregations]}
                        onTagRemove={handleTagRemove}
                    />
                    <Fragment rendered={props.options !== undefined && !props.disabled}>
                        <div className={"columns mt-3"}>
                            <div className={"column is-4"}>
                                <Select options={options}
                                        optionsCode={"name"}
                                        optionsLabel={"name"}
                                        optionsValue={"name"}
                                        disabled={options?.length === 0}
                                        isInvalid={getField("column").error !== undefined && getField("column").error !== ""}
                                        invalidMessage={getField("column").error}
                                        selectedItem={values.column}
                                        label={t('data.column')}
                                        emptyOption={true}
                                        onChange={(e) => {
                                            const value = e !== "" ? e : undefined;
                                            clearValidation();
                                            getField("column").setValue(value, value ? "column" : undefined, false);
                                            getField("operation").setValue(null, undefined, false);
                                            getField("label").setValue(null, undefined, false);
                                        }}/>
                            </div>
                            <div className={"column is-2"}>
                                <Select options={getAggregationOperations()}
                                        isInvalid={getField("operation").error !== undefined && getField("operation").error !== ""}
                                        invalidMessage={getField("operation").error}
                                        disabled={getAggregationOperations().length === 0}
                                        onChange={(e) => {

                                            let value = "" + e;
                                            value = value.toLowerCase();
                                            clearValidation();

                                            getField("operation").setValue(e, "operation", false);
                                            getField("label")
                                                .setValue(("" + camelCase("" + value + "_" + getField("column").value)), "label", false);
                                        }}
                                        selectedItem={getField("operation").value}
                                        label={t('data.operation')}
                                        optionsCode={"code"} optionsLabel={"id"} optionsValue={"code"}
                                />
                            </div>
                            <div className={"column is-4"}>
                                <Input label={t('data.label')}
                                       value={getField("label").value}
                                       disabled={getAggregationOperations().length === 0 || !getField("column").value || !getField("operation").value}
                                       isInvalid={getField("label").error !== undefined && getField("label").error !== ""}
                                       invalidMessage={getField("label").error}
                                       onChange={(value) => {
                                           clearValidation();
                                           getField("label").setValue(value, "label", false);
                                       }}
                                />

                            </div>
                            <div className={"column is-2"}>
                                <Button label={t('add')} className={"is-outlined"} onClick={onAdd}/>
                            </div>
                        </div>
                    </Fragment>
                </div>
            </div>
        </div>
    );
}

AggregationsCard.propTypes = {
    options: PropTypes.array,
    formField: PropTypes.object,
    disabled: PropTypes.bool.isRequired,
    restrictedLabels: PropTypes.array
}

AggregationsCard.defaultProps = {
    disabled: false,
    restrictedLabels: []
}

export default AggregationsCard;
